import { Suspense, useEffect, useState } from 'react';
import { HashRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { getAuthProvider } from '@/auth';
import { shouldAskNickname } from '@/auth/onboarding';
import {
  inviteCodeFromAppUrl,
  inviteCodeFromHash,
  stashInvite,
  takeStashedInvite,
} from '@/auth/pendingInvite';
import { detectInApp, externalOpenUrl } from '@/domain/inAppBrowser';
import { platform } from '@/platform';
import { cacheLoadedAssets } from '@/platform/cacheAssets';
import { APP_SCHEME } from '@/platform/native';
import { useT } from '@/i18n';
import { useAuthStore } from '@/store/authStore';
import { useTripStore } from '@/store/tripStore';
import { HomeScreen } from '@/screens/HomeScreen';
import {
  ActivityScreen,
  ChecklistScreen,
  ExpensesScreen,
  GuideScreen,
  IdeasScreen,
  InviteScreen,
  ItemDetailScreen,
  ItemEditScreen,
  MapScreen,
  MembersScreen,
  preloadScreens,
  PrivacyScreen,
  ProfileScreen,
  TripCreateScreen,
} from '@/screens/lazy';
import { TripScreen } from '@/screens/TripScreen';
import { NicknameSetupScreen } from '@/screens/NicknameSetupScreen';
import { OfflineBar } from '@/components/OfflineBar';
import { OpenInBrowserScreen } from '@/screens/OpenInBrowserScreen';
import { SignInScreen } from '@/screens/SignInScreen';
import { TabFallbackScreen } from '@/screens/TabFallbackScreen';
import { UnavailableScreen } from '@/screens/UnavailableScreen';
import { hasBackend } from '@/supabase/client';

/**
 * HashRouter를 쓰는 이유 — Capacitor 전환 제약 #1·#2.
 *
 * 네이티브 웹뷰에서는 index.html이 로컬에서 로드되므로 서버가 모든 경로를
 * index.html로 되돌려주는 동작을 기대할 수 없다. BrowserRouter는 환경에 따라
 * 새로고침·딥링크에서 404가 되고, 해시 라우팅은 어디서든 동일하게 동작한다.
 * 웹에서 경로를 깔끔하게 만들고 싶어지면 그때 웹 전용으로 바꾸면 되지만,
 * 앱까지 하나로 가려면 해시가 안전하다.
 */
/**
 * 편집 화면을 항목마다 새로 만든다.
 *
 * 두 라우트가 같은 컴포넌트를 가리키기 때문에, 수정 화면에서 추가 화면으로
 * (또는 다른 항목 수정으로) 넘어가도 React가 같은 인스턴스를 재사용한다.
 * 입력값은 useState 초기값으로 한 번만 읽으므로, 그대로 두면 "일정 추가"를
 * 눌렀는데 직전에 고치던 항목의 내용이 남는다.
 *
 * key를 주면 항목이 바뀔 때 언마운트되고 초기값을 다시 읽는다.
 */
function ItemEditRoute() {
  const { itemId } = useParams();
  return <ItemEditScreen key={itemId ?? 'new'} />;
}

/**
 * 초대 화면을 코드마다 새로 만든다.
 *
 * 편집 화면과 같은 문제다(ItemEditRoute). 주소의 코드만 바뀌면 React가 같은
 * 인스턴스를 재사용해서, 입력칸에는 이전 코드가, 오류에는 이전 실패가 남는다.
 * "한 번만 자동 참가" 가드도 이미 켜진 채라 새 코드로는 시도조차 하지 않는다.
 * 두 번째 초대 링크를 연 사람이 그대로 겪는 상황이다.
 */
function InviteRoute() {
  const { code } = useParams();
  return <InviteScreen key={code ?? 'manual'} />;
}

/** 따로 받는 화면이 도착하기 전 — 대개 한순간이라 글자만 */
function ScreenLoading() {
  const t = useT();
  return (
    <div className="app">
      <main className="main main--no-tabs">
        <p className="empty">{t.common.loading}</p>
      </main>
    </div>
  );
}

export function App() {
  const loading = useAuthStore((s) => s.loading);
  const account = useAuthStore((s) => s.account);
  const restore = useAuthStore((s) => s.restore);
  const t = useT();

  /*
   * 로그인 전 화면(로그인·처리방침)은 라우터 밖이라 주소가 바뀌어도 다시 그려지지
   * 않는다. 해시를 직접 지켜본다. 로그인 뒤에는 HashRouter가 맡는다.
   */
  const [hash, setHash] = useState(() => window.location.hash);
  useEffect(() => {
    const onHash = () => setHash(window.location.hash);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const loadTrips = useTripStore((s) => s.load);

  /**
   * 첫 로그인 닉네임 화면을 이번 로그인에서 끝냈는지. 계정이 바뀌면 다시 판단한다.
   * 판단 자체는 auth/onboarding.ts — 여기는 화면을 넘기기만 한다.
   */
  const [nicknameDoneFor, setNicknameDoneFor] = useState<string | null>(null);

  useEffect(() => {
    void restore();
  }, [restore]);

  /*
   * 로그인한 뒤에 읽는다. 로그인 전에 부르면 RLS가 전부 막아 빈 목록이
   * 돌아오고, 그 결과가 "여행이 없습니다"로 화면에 굳는다.
   */
  const subscribeTrips = useTripStore((s) => s.subscribe);

  useEffect(() => {
    if (account) void loadTrips(account.id);
  }, [account, loadTrips]);

  /*
   * 연결이 돌아오면 새로 읽는다. 오프라인 동안은 사본을 보여줬으니(OfflineBar),
   * 친구들이 그사이 고친 것을 받아 와야 한다.
   */
  useEffect(() => {
    if (!account) return;
    const reload = () => void loadTrips(account.id);
    window.addEventListener('online', reload);
    return () => window.removeEventListener('online', reload);
  }, [account, loadTrips]);

  /*
   * 다른 사람의 변경을 받는다. 로그인한 동안만 구독하고, 로그아웃하면 끊는다 —
   * 안 끊으면 다음 사람이 이 기기로 로그인했을 때 앞사람 몫의 이벤트가 섞인다.
   */
  useEffect(() => {
    if (!account) return;
    return subscribeTrips();
  }, [account, subscribeTrips]);

  /*
   * 첫 화면이 뜬 뒤 남는 시간에 나머지 화면을 미리 받아 둔다 — 누를 때 기다리지 않게,
   * 그리고 오프라인에서도 열리게(서비스 워커가 저장). screens/lazy.ts 참고.
   */
  useEffect(() => {
    if (!account) return;
    const idle =
      window.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1500));
    idle(() => {
      void preloadScreens().then(() => cacheLoadedAssets());
    });
  }, [account]);

  /*
   * 초대 링크를 연 사람이 로그인하고 돌아왔으면 그 초대로 다시 보낸다.
   * (왜 필요한지는 auth/pendingInvite.ts)
   */
  useEffect(() => {
    if (!account) return;
    const code = takeStashedInvite();
    if (code) window.location.hash = `#/invite/${encodeURIComponent(code)}`;
  }, [account]);

  /*
   * 앱이 초대 딥링크로 열렸을 때. 로그인돼 있으면 바로 참가 화면으로,
   * 아니면 로그인하는 동안 보관했다가 위의 효과가 꺼내 쓴다.
   */
  useEffect(() => {
    if (!platform.onDeepLink) return;
    return platform.onDeepLink((url) => {
      const code = inviteCodeFromAppUrl(url, APP_SCHEME);
      if (!code) return;
      if (useAuthStore.getState().account) {
        window.location.hash = `#/invite/${encodeURIComponent(code)}`;
      } else {
        stashInvite(code);
      }
    });
  }, []);

  /*
   * 세션 복구가 끝나기 전에는 아무것도 결정하지 않는다. 바로 로그인 화면을
   * 띄우면 이미 로그인한 사람에게도 한 번 깜빡이고 지나간다.
   */
  /*
   * 앱 안 브라우저(카카오톡·라인·인스타그램 등)에서 열렸다 — Google이 여기서는 로그인을
   * 막는다(domain/inAppBrowser.ts). 기본 브라우저로 옮길 수 있으면 로그인 여부와 상관없이
   * 바로 옮긴다. 강제할 수 없는 곳(iOS의 인스타그램 등)은 아래 로그인 화면 대신 안내한다.
   * 앱(Capacitor)은 해당 없다.
   */
  const ua = navigator.userAgent;
  const inApp = platform.kind === 'web' ? detectInApp(ua) : null;
  if (inApp && externalOpenUrl(inApp, window.location.href, ua)) {
    return <OpenInBrowserScreen kind={inApp} ua={ua} />;
  }

  if (loading) {
    return (
      <div className="app">
        <main className="main main--no-tabs">
          <p className="empty">{t.common.loading}</p>
        </main>
      </div>
    );
  }

  // 배포 빌드인데 백엔드 설정이 빠졌다 — 개발용 목으로 몰래 돌지 않는다
  if (!hasBackend && import.meta.env.PROD) return <UnavailableScreen />;

  if (!account) {
    /*
     * 처리방침은 로그인 전에도 읽을 수 있어야 한다 — 가입하기 전에 읽는 문서다.
     * 사용법도 — 초대받은 친구가 로그인하기 전에 무슨 앱인지 본다.
     * 로그인 전 화면은 라우터 밖이라, 이 경우만 라우터로 감싸 띄운다.
     */
    if (hash.startsWith('#/privacy') || hash.startsWith('#/guide')) {
      return (
        <HashRouter>
          <Suspense fallback={<ScreenLoading />}>
            {hash.startsWith('#/guide') ? <GuideScreen /> : <PrivacyScreen />}
          </Suspense>
        </HashRouter>
      );
    }
    // 로그인이 막히는 앱 안 브라우저 — Google 버튼을 누르기 전에 알려 준다
    if (inApp) return <OpenInBrowserScreen kind={inApp} ua={ua} />;

    // 초대 링크로 들어왔다면 로그인 동안 코드를 붙잡아 둔다
    const code = inviteCodeFromHash(window.location.hash);
    if (code) stashInvite(code);
    return <SignInScreen methods={getAuthProvider().methods} invited={Boolean(code)} />;
  }

  if (nicknameDoneFor !== account.id && shouldAskNickname(account)) {
    return (
      <NicknameSetupScreen
        userId={account.id}
        onDone={() => setNicknameDoneFor(account.id)}
      />
    );
  }

  return (
    <HashRouter>
      <OfflineBar />
      {/* 따로 받는 화면(screens/lazy.ts)이 도착할 때까지 */}
      <Suspense fallback={<ScreenLoading />}>
      <Routes>
        <Route path="/" element={<HomeScreen />} />
        {/* 여행 밖에서 누른 일정·지도 탭 — 볼 여행으로 넘기거나, 없으면 빈 화면 */}
        <Route path="/schedule" element={<TabFallbackScreen kind="schedule" />} />
        <Route path="/map" element={<TabFallbackScreen kind="map" />} />
        {/* 'new'가 :tripId로 잡히지 않도록 먼저 선언한다 */}
        <Route path="/trip/new" element={<TripCreateScreen />} />
        <Route path="/trip/:tripId" element={<TripScreen />} />
        {/* 'new'가 :itemId로 잡히지 않도록 상세보다 먼저 선언한다 */}
        <Route path="/trip/:tripId/item/new" element={<ItemEditRoute />} />
        <Route path="/trip/:tripId/item/:itemId/edit" element={<ItemEditRoute />} />
        <Route path="/trip/:tripId/item/:itemId" element={<ItemDetailScreen />} />
        <Route path="/trip/:tripId/map" element={<MapScreen />} />
        <Route path="/trip/:tripId/checklist" element={<ChecklistScreen />} />
        <Route path="/trip/:tripId/expenses" element={<ExpensesScreen />} />
        <Route path="/trip/:tripId/ideas" element={<IdeasScreen />} />
        <Route path="/trip/:tripId/members" element={<MembersScreen />} />
        <Route path="/trip/:tripId/activity" element={<ActivityScreen />} />
        <Route path="/profile" element={<ProfileScreen />} />
        <Route path="/privacy" element={<PrivacyScreen />} />
        <Route path="/guide" element={<GuideScreen />} />
        <Route path="/invite" element={<InviteRoute />} />
        <Route path="/invite/:code" element={<InviteRoute />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </HashRouter>
  );
}
